import React, { FunctionComponent, useRef } from 'react';
import { ApolloError } from 'apollo-boost';
import { Container, Header } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { reject, isEmpty, isNil, pathOr } from 'ramda';

import Query from 'components/Query';
import { CSVDiscoveredVenueFields } from 'scenes/Home/Home.helpers';
import DownloadWidget from 'components/DownloadWidget';
import { ChartPlaceholder } from 'components/Placeholder';
import { ClassProp } from 'helpers/types';

import DiscoveredVenueTable from 'scenes/Home/components/DiscoveredVenueTable';
import { DiscoveredVenue } from 'scenes/Home/components/DiscoveredVenueTable/DiscoveredVenueTable';

import { GET_DISCOVERED_VENUE_SEARCH_RESULTS } from './SearchResults.queries';

const styles = {
  container: {
    margin: '2.25em auto !important',
    height: 'auto !important',
  },
  header: {
    display: 'flex !important',
    paddingTop: '1.875em !important',
  },
};

type QueryResponse = {
  searchDiscoveredVenues: DiscoveredVenue[];
  loading?: boolean;
  error?: ApolloError;
};

type Props = {
  classes: ClassProp<typeof styles>;
  searchType: string;
  searchText: string;
  cnidsToFilter: string;
  filter: string;
};

const renderDiscoveredVenueTable = (
  venues: DiscoveredVenue[],
  csvName: string,
  contentRef: React.MutableRefObject<null>,
) => (
  <>
    <DownloadWidget
      nodeRef={contentRef}
      imageName={csvName}
      data={venues}
      fields={CSVDiscoveredVenueFields}
      csvName={csvName}
    />

    <div ref={contentRef}>
      <DiscoveredVenueTable data={venues} />
    </div>
  </>
);

const isNotValid = (data: DiscoveredVenue[]) => isEmpty(data) || isNil(data);

const renderNoVenues = (classes: ClassProp<typeof styles>) => (
  <Header as="h3" className={classes.header} content="No Venues found!" />
);

const renderVMIDSearchResults = (
  classes: ClassProp<typeof styles>,
  searchType: string,
  searchText: string,
  contentRef: React.MutableRefObject<null>,
) => (response: QueryResponse) => {
  const discoveredVenues = pathOr(
    [],
    ['data', 'searchDiscoveredVenues'],
    response,
  ) as DiscoveredVenue[];

  if (response.loading) {
    return <ChartPlaceholder />;
  }

  if (isNotValid(discoveredVenues)) {
    return renderNoVenues(classes);
  }

  return (
    <Container className={classes.container}>
      {renderDiscoveredVenueTable(
        discoveredVenues,
        `${searchType} - ${searchText}_venues`,
        contentRef,
      )}
    </Container>
  );
};

const VenusSearchResults: FunctionComponent<Props> = ({
  classes,
  searchText,
  searchType,
}) => {
  /*
   ** Values are left hardcoded since we will only be using this functionality with VISA
   ** and will most likely depreciate this feature once we reach the 5th phase of our venue
   ** discovery process
   */

  const params = reject(isEmpty, {
    cardNetwork: 'VISA',
    cardNetworkExternalIdType: 'VMID',
    cardNetworkExternalId: searchText,
  });

  const contentRef = useRef(null);

  return (
    <Query
      enableProgressInfo
      query={GET_DISCOVERED_VENUE_SEARCH_RESULTS}
      variables={{
        params,
      }}
      render={renderVMIDSearchResults(
        classes,
        searchType,
        searchText,
        contentRef,
      )}
    />
  );
};

export default withStyles(styles)(VenusSearchResults);
